<script>
import { mapActions, mapGetters, mapState } from 'vuex';

import { formatAmount } from '../lib/formatters';
import { PaymentMethods } from '../';

import CustomerFieldInput from './customer_field_input.vue';
import CustomerFieldSelect from './customer_field_select.vue';
import CustomerFieldDateofbirth from './customer_field_dateofbirth.vue';

export default {
  components: {
    CustomerFieldInput,
    CustomerFieldSelect,
    CustomerFieldDateofbirth,
  },

  computed: {
    ...mapState([
      'countries',
      'psps',
      'selectedPspCode',
      'required',
      'optional'
    ]),

    ...mapState({
      countryId: (state) => state.inputs.countryId.value,
      vatno: (state) => state.inputs.vatno.value,
      userCountryId: (state) => state.product.userCountryId
    }),

    ...mapGetters([
      'payInInstallments',
      'selectedAmounts'
    ]),

    afterPaySelected() {
      return this.selectedPspCode === PaymentMethods.AFTERPAY;
    },

    directDebitSelected() {
      return this.selectedPspCode === PaymentMethods.SEPA_DIRECT_DEBIT;
    },

    showIBANField() {
      if (this.payInInstallments) {
        return [
          PaymentMethods.SEPA_DIRECT_DEBIT,
          PaymentMethods.BANCONTACT
        ].includes(this.selectedPspCode);
      }

      return this.selectedPspCode === PaymentMethods.SEPA_DIRECT_DEBIT;
    },

    vatAmount() {
      return this.selectedAmounts.vat.amount;
    },

    formattedVatAmount() {
      return formatAmount(this.vatAmount);
    },

    hasAfterPay() {
      return this.psps.some((psp) => psp.code === PaymentMethods.AFTERPAY);
    },

    isForeignCountry() {
      return this.countryId != this.userCountryId;
    },

    inlineNameFields() {
      return (this.required.firstname && this.required.name) || this.afterPaySelected;
    },

    emailIsFocused() {
      return !(this.required.name || this.required.firstname || this.afterPaySelected || this.directDebitSelected);
    }
  },

  mounted() {
    this.countryChanged();
  },

  methods: {
    ...mapActions([
      'setPspDisabled'
    ]),

    countryChanged() {
      if (!this.hasAfterPay) { return; }

      this.setPspDisabled({ code: PaymentMethods.AFTERPAY, value: this.isForeignCountry });
    },
  }
};
</script>

<template>
  <div>
    <h3 class="mb-4">
      {{ t('pay.js.marketing.payment_information.title') }}
    </h3>

    <div
      v-if="inlineNameFields"
      class="row mb-4"
    >
      <div class="col-md-6 mb-4 mb-md-0">
        <customer-field-input
          name="firstname"
          :focused-element="true"
        />
      </div>

      <div class="col-md-6">
        <customer-field-input
          name="name"
          label="lastname"
        />
      </div>
    </div>

    <div
      v-else-if="required.firstname"
      class="row mb-4"
    >
      <div class="col">
        <customer-field-input
          name="firstname"
          :focused-element="true"
          :optional="optional.firstname && !directDebitSelected"
        />
      </div>
    </div>

    <div
      v-else-if="required.name || directDebitSelected"
      class="row mb-4"
    >
      <div class="col">
        <customer-field-input
          name="name"
          :focused-element="true"
          :optional="optional.name && !directDebitSelected"
        />
      </div>
    </div>

    <div class="row mb-4">
      <div class="col">
        <customer-field-input
          name="email"
          :focused-element="emailIsFocused"
        />
      </div>
    </div>

    <div
      v-if="required.company"
      class="row mb-4"
    >
      <div class="col">
        <customer-field-input
          name="companyName"
          :optional="optional.companyName"
        />
      </div>
    </div>

    <div
      v-if="showIBANField"
      class="row mb-4"
    >
      <div class="col">
        <customer-field-input
          name="accountno"
        />
      </div>
    </div>

    <input
      v-if="vatno"
      :value="vatno"
      type="hidden"
      name="transaction_details[vatno]"
    >

    <div
      v-if="required.vat"
      class="row mb-4"
    >
      <div class="col">
        <customer-field-input
          name="vatno"
          :optional="optional.vatno"
        />
      </div>

      <div
        v-if="vatAmount > 0 && isForeignCountry"
        class="c-form__field"
      >
        <div class="c-form__feedback">
          {{ t('pay.js.marketing.payment_information.vat_number_message', {
            amount: formattedVatAmount
          }) }}
        </div>
      </div>
    </div>

    <customer-field-dateofbirth v-if="afterPaySelected" />

    <div v-if="required.password">
      <h3 class="mb-4">
        {{ t('pay.js.marketing.payment_information.authentication.title') }}
      </h3>

      <div class="row mb-4">
        <div class="col">
          <customer-field-input
            name="password"
            type="password"
            :optional="optional.password"
          />
        </div>
      </div>

      <div class="row mb-4">
        <div class="col">
          <customer-field-input
            name="passwordConfirmation"
            type="password"
            :optional="optional.passwordConfirmation"
          />
        </div>
      </div>
    </div>

    <div v-if="required.address || afterPaySelected">
      <h3 class="mb-4">
        {{ t('pay.js.marketing.payment_information.address.title') }}
      </h3>

      <template v-if="required.address || afterPaySelected">
        <div class="row mb-4">
          <div class="col">
            <customer-field-input
              name="address"
              :optional="optional.address && !afterPaySelected"
            />
          </div>
        </div>

        <div class="row mb-4">
          <div class="col-md-6 mb-4 mb-md-0">
            <customer-field-input
              name="zipcode"
              :optional="optional.zipcode && !afterPaySelected"
            />
          </div>

          <div class="col-md-6">
            <customer-field-input
              name="city"
              :optional="optional.city && !afterPaySelected"
            />
          </div>
        </div>
      </template>

      <div class="row mb-4">
        <div class="col">
          <customer-field-select
            name="countryId"
            autocomplete="off"
            @changed="countryChanged"
          >
            <option
              v-for="country in countries"
              :key="country.value"
              :value="country.value"
            >
              {{ country.name }}
            </option>
          </customer-field-select>
        </div>
      </div>
    </div>

    <div
      v-if="required.phone || afterPaySelected"
      class="row mb-4"
    >
      <div class="col">
        <customer-field-input
          name="phoneno"
          :optional="optional.phoneno && !afterPaySelected"
        />
      </div>
    </div>

    <div
      v-if="required.mobile"
      class="row mb-4"
    >
      <div class="col">
        <customer-field-input
          name="mobile"
          :optional="optional.mobile"
        />
      </div>
    </div>

    <div
      v-if="required.cocNumber"
      class="row mb-4"
    >
      <div class="col">
        <customer-field-input
          name="cocNumber"
          :optional="optional.cocNumber"
        />
      </div>
    </div>

    <div
      v-if="required.remark"
      class="row mb-4"
    >
      <div class="col">
        <customer-field-input
          name="remarks"
          :optional="optional.remarks"
          type="textarea"
        />
      </div>
    </div>
  </div>
</template>
